import React from 'react';

import { CloseDetailDialog } from '@epi-shared/components';
import {
  Box,
  Button,
  ButtonMode,
  ButtonSize,
  Checkbox,
  CloseIcon,
  Dialog,
  FormControlLabel
} from 'edenred-ui';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { StyledDialog } from './BeneficiariesModal.styles';
import BeneficiariesModalContent from './BeneficiariesModalContent';
import { useBeneficiaryForm } from './hooks/useBeneficiaryForm';

type OwnProps = {
  isShowModal: boolean;
  onClose: () => void;
  beneficiaryId: string | null;
  setIsEmployeeAdded: (isEmployeeAdded: boolean) => void;
};

const BeneficiariesModal = ({
  isShowModal,
  onClose,
  beneficiaryId,
  setIsEmployeeAdded
}: OwnProps) => {
  const { t } = useTranslation();

  const {
    form,
    submit,
    disableSubmit,
    isProcessing,
    handleClose,
    showCloseConfirmation,
    confirmCloseWithoutSaving,
    confirmSaving,
    addAnotherEmployee,
    toggleAddAnotherEmployee
  } = useBeneficiaryForm({
    beneficiaryId,
    onClose,
    isShowModal,
    setIsEmployeeAdded
  });

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const { handleSubmit } = form;
  const { sizeExtraExtraLarge, gray85 } = useTheme();

  return (
    <StyledDialog
      open={isShowModal}
      onClose={handleClose}
      disablePortal
      onKeyDown={handleKeyDown}
    >
      <Dialog.Title>
        <Box position="absolute" right={16} top={16}>
          <Button
            id="beneficiaries-modal-close-btn"
            mode={ButtonMode.Link}
            onClick={handleClose}
          >
            <CloseIcon />
          </Button>
        </Box>
        <Box fontSize={sizeExtraExtraLarge} fontWeight={700} color={gray85}>
          {t(
            `containers.beneficiaries.${
              beneficiaryId ? 'detail.edit_title' : 'add_employee.main_action'
            }`
          )}
        </Box>
      </Dialog.Title>
      <Dialog.Content>
        <FormProvider {...form}>
          <BeneficiariesModalContent
            beneficiaryId={beneficiaryId}
            handleSubmit={handleSubmit}
            submit={submit}
            isShowModal={isShowModal}
          />
        </FormProvider>
      </Dialog.Content>
      <Dialog.Actions>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          {beneficiaryId ? (
            <Box />
          ) : (
            <Box display="flex" alignItems="flex-start" mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addAnotherEmployee}
                    onChange={toggleAddAnotherEmployee}
                  />
                }
                label={t('containers.beneficiaries.add_employee.add_another')}
                labelPlacement="end"
              />
            </Box>
          )}
          <Box display="flex" gap={2}>
            <Button
              id="cancel-benefit-settings"
              onClick={handleClose}
              mode={ButtonMode.Secondary}
              size={ButtonSize.Medium}
              minWidth={135}
              style={{ border: 'none', boxShadow: 'none' }}
            >
              {t(`components.manage_benefit_lunch.detail_action_cancel`)}
            </Button>
            <Button
              id="save-benefit-settings"
              mode={ButtonMode.Primary}
              size={ButtonSize.Medium}
              minWidth={135}
              type="submit"
              form="beneficiary-form"
              disabled={disableSubmit}
              isProcessing={isProcessing}
            >
              {t(`components.manage_benefit_lunch.detail_action_save`)}
            </Button>
          </Box>
        </Box>
      </Dialog.Actions>
      <CloseDetailDialog
        isOpen={showCloseConfirmation}
        title={t('containers.beneficiaries.close_detail_dialog.title')}
        handleCloseModal={confirmCloseWithoutSaving}
        primaryAction={confirmCloseWithoutSaving}
        secondaryAction={confirmSaving}
        disableSubmit={disableSubmit}
        secondaryActionText={t(
          'containers.beneficiaries.close_detail_dialog.save'
        )}
        primaryActionText={t(
          'containers.beneficiaries.close_detail_dialog.close_without_saving'
        )}
        contentText={t('containers.beneficiaries.close_detail_dialog.content')}
      />
    </StyledDialog>
  );
};

export default BeneficiariesModal;
